import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DeviceDto } from 'src/app/models/device.model';
import { humanizeEnumValue } from 'src/app/utils/enum-display';
import {
  DashboardServiceDuePerDroneItem,
  ServiceDuePart,
  UAV,
} from '../../../models/report.model';
import {
  ColumnDefinition,
  DataColumnDefinition,
} from '../../qs-table/column-definition.interface';

type DashboardServiceDueTableRow = {
  rank: number;
  device: DeviceDto;
  uav: UAV;
  partId: string;
  partType: string;
  partSubType: string;
  metricType: string;
  usageRatioPercent: number;
  valueLabel: string;
  thresholdLabel: string;
  remainingLabel: string;
};

type ServiceDueLeadingColumn = 'uav' | 'partId';

@Component({
  selector: 'app-dashboard-service-due-list',
  templateUrl: './dashboard-service-due-list.component.html',
  styleUrls: ['./dashboard-service-due-list.component.scss'],
  standalone: false,
})
export class DashboardServiceDueListComponent implements OnInit, OnChanges {
  @Input() title = '';
  @Input() subtitle = '';
  @Input() items: DashboardServiceDuePerDroneItem[] = [];
  @Input() accent = '#0f766e';
  @Input() leadingColumn: ServiceDueLeadingColumn = 'uav';

  isCollapsed = false;
  tableRows: DashboardServiceDueTableRow[] = [];
  columns: ColumnDefinition<DashboardServiceDueTableRow>[] = [];
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.columns = this.buildColumns();
    this.rebuildRows();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns = this.buildColumns();
      });
  }

  ngOnChanges(): void {
    this.columns = this.buildColumns();
    this.rebuildRows();
  }

  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  private buildColumns(): ColumnDefinition<DashboardServiceDueTableRow>[] {
    const leadingEntityColumn =
      this.leadingColumn === 'partId'
        ? this.buildPartIdColumn()
        : this.buildUavColumn();
    const trailingEntityColumn =
      this.leadingColumn === 'partId'
        ? this.buildUavColumn()
        : this.buildPartIdColumn();

    return [
      {
        type: 'data',
        field: 'rank',
        header: '#',
        width: '70px',
        sortFn: (
          a: DashboardServiceDueTableRow,
          b: DashboardServiceDueTableRow,
          multiplier: number,
        ) => (a.rank - b.rank) * multiplier,
      } as DataColumnDefinition<DashboardServiceDueTableRow>,
      leadingEntityColumn,
      trailingEntityColumn,
      {
        type: 'data',
        field: 'partType',
        header: this.translate.instant('dashboard.table.part_type'),
        width: '280px',
        sortFn: (
          a: DashboardServiceDueTableRow,
          b: DashboardServiceDueTableRow,
          multiplier: number,
        ) => a.partType.localeCompare(b.partType) * multiplier,
      } as DataColumnDefinition<DashboardServiceDueTableRow>,
      {
        type: 'data',
        field: 'partSubType',
        header: this.translate.instant('dashboard.table.part_sub_type'),
        width: '320px',
        sortFn: (
          a: DashboardServiceDueTableRow,
          b: DashboardServiceDueTableRow,
          multiplier: number,
        ) => a.partSubType.localeCompare(b.partSubType) * multiplier,
      } as DataColumnDefinition<DashboardServiceDueTableRow>,
      {
        type: 'data',
        field: 'metricType',
        header: this.translate.instant('dashboard.table.metric_type'),
        width: '220px',
        sortFn: (
          a: DashboardServiceDueTableRow,
          b: DashboardServiceDueTableRow,
          multiplier: number,
        ) => a.metricType.localeCompare(b.metricType) * multiplier,
      } as DataColumnDefinition<DashboardServiceDueTableRow>,
      {
        type: 'data',
        field: 'usageRatioPercent',
        header: this.translate.instant('dashboard.table.usage_percent'),
        width: '120px',
        sortFn: (
          a: DashboardServiceDueTableRow,
          b: DashboardServiceDueTableRow,
          multiplier: number,
        ) => (a.usageRatioPercent - b.usageRatioPercent) * multiplier,
        format: (row?: DashboardServiceDueTableRow): string =>
          `${(row?.usageRatioPercent ?? 0).toFixed(2)}%`,
        style: (row: DashboardServiceDueTableRow) => {
          if (row.usageRatioPercent > 80) {
            return { color: '#b91c1c', 'font-weight': '600' };
          }

          if (row.usageRatioPercent > 60) {
            return { color: '#a16207', 'font-weight': '600' };
          }

          return {};
        },
      } as DataColumnDefinition<DashboardServiceDueTableRow>,
      {
        type: 'data',
        field: 'valueLabel',
        header: this.translate.instant('dashboard.table.current'),
        width: '140px',
      } as DataColumnDefinition<DashboardServiceDueTableRow>,
      {
        type: 'data',
        field: 'thresholdLabel',
        header: this.translate.instant('dashboard.table.threshold'),
        width: '140px',
      } as DataColumnDefinition<DashboardServiceDueTableRow>,
      {
        type: 'data',
        field: 'remainingLabel',
        header: this.translate.instant('dashboard.table.remaining'),
        width: '140px',
      } as DataColumnDefinition<DashboardServiceDueTableRow>,
    ];
  }

  private buildUavColumn(): DataColumnDefinition<DashboardServiceDueTableRow> {
    const shouldEmphasizeUav = this.leadingColumn === 'uav';

    return {
      type: 'data',
      field: 'uav',
      header: this.translate.instant('dashboard.table.uav'),
      width: '260px',
      sortFn: (
        a: DashboardServiceDueTableRow,
        b: DashboardServiceDueTableRow,
        multiplier: number,
      ) =>
        this.formatUavLabel(a.uav).localeCompare(this.formatUavLabel(b.uav)) *
        multiplier,
      format: (row?: DashboardServiceDueTableRow): string =>
        this.formatUavLabel(row?.uav ?? {}),
      tooltip: (row: DashboardServiceDueTableRow): string =>
        this.formatUavTooltip(row.uav),
      onClick: (row: DashboardServiceDueTableRow): void => {
        this.navigateToDroneDetails(row.device);
      },
      style: (row: DashboardServiceDueTableRow) => ({
        'font-weight': shouldEmphasizeUav ? '600' : '400',
        cursor: this.hasDeviceId(row.device) ? 'pointer' : 'default',
      }),
    };
  }

  private buildPartIdColumn(): DataColumnDefinition<DashboardServiceDueTableRow> {
    const shouldEmphasizePartId = this.leadingColumn === 'partId';

    return {
      type: 'data',
      field: 'partId',
      header: this.translate.instant('dashboard.table.part_id'),
      width: '180px',
      sortFn: (
        a: DashboardServiceDueTableRow,
        b: DashboardServiceDueTableRow,
        multiplier: number,
      ) => a.partId.localeCompare(b.partId) * multiplier,
      style: () => ({
        'font-weight': shouldEmphasizePartId ? '600' : '400',
      }),
    };
  }

  private rebuildRows(): void {
    const topItems = this.items.slice(0, 10);

    this.tableRows = topItems.map((item, index) => ({
      rank: index + 1,
      device: item.device,
      uav: item.uav,
      partId: this.formatPartId(item.partId),
      partType: humanizeEnumValue(item.partId.partType, '-'),
      partSubType: humanizeEnumValue(item.partId.partSubType, '-'),
      metricType: humanizeEnumValue(item.metricType, '-'),
      usageRatioPercent: Math.max(0, item.usageRatio * 100),
      valueLabel: this.formatQuantity(item.value, item.valueUnit),
      thresholdLabel: this.formatQuantity(
        item.valueThreshold,
        item.valueThresholdUnit,
      ),
      remainingLabel: this.formatRemaining(item),
    }));
  }

  private formatPartId(part: ServiceDuePart): string {
    return part.id || '-';
  }

  private formatQuantity(value: number, unit: string): string {
    const safeValue = Number.isFinite(value) ? value : 0;

    if (this.isMinuteUnit(unit)) {
      return this.formatMinutesDuration(safeValue);
    }

    const formatted = safeValue.toFixed(2);
    return unit ? `${formatted} ${unit}` : formatted;
  }

  private formatRemaining(item: DashboardServiceDuePerDroneItem): string {
    const remaining = item.valueThreshold - item.value;
    const unit = item.valueThresholdUnit || item.valueUnit;
    return this.formatQuantity(remaining, unit);
  }

  private isMinuteUnit(unit: string | undefined): boolean {
    if (!unit) {
      return false;
    }

    const normalizedUnit = unit.trim().toLowerCase();
    return ['minute', 'minutes', 'min', 'mins'].includes(normalizedUnit);
  }

  private formatMinutesDuration(totalMinutes: number): string {
    const roundedMinutes = Math.round(Math.abs(totalMinutes));
    const sign = totalMinutes < 0 && roundedMinutes > 0 ? '-' : '';
    const minutesInDay = 24 * 60;
    const days = Math.floor(roundedMinutes / minutesInDay);
    const hours = Math.floor((roundedMinutes % minutesInDay) / 60);
    const minutes = roundedMinutes % 60;
    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days}d`);
    }

    if (hours > 0 || days > 0) {
      parts.push(`${hours}h`);
    }

    parts.push(`${minutes}m`);
    return `${sign}${parts.join(' ')}`;
  }

  // The cell already shows unit and model, so the tooltip adds the operator-assigned name when
  // there is one. Falling back to an empty string leaves the table's default tooltip in place.
  private formatUavTooltip(uav: UAV): string {
    return uav.nameAlias ?? '';
  }

  private formatUavLabel(uav: UAV): string {
    const hardwareRevision = this.toFiniteNumber(uav['hardwareRevision']);
    const unitId = this.toFiniteNumber(uav['unitId']);
    // Reports are always queried with resolveDevices=true, so the model name comes from the device
    // the backend resolved. There is deliberately no local hardware-revision lookup table here: it
    // would be a second, drifting copy of the backend model registry.
    const modelName = uav.modelName;

    if (unitId !== null && modelName) {
      return `${unitId} (${modelName})`;
    }

    if (unitId !== null && hardwareRevision !== null) {
      return `${unitId} (HR ${hardwareRevision})`;
    }

    if (unitId !== null) {
      return `${unitId}`;
    }

    if (modelName) {
      return modelName;
    }

    if (hardwareRevision !== null) {
      return `HR ${hardwareRevision}`;
    }

    return Object.keys(uav).length > 0
      ? JSON.stringify(uav)
      : this.translate.instant('dashboard.labels.unknown_uav');
  }

  private toFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private navigateToDroneDetails(device: DeviceDto): void {
    if (!this.hasDeviceId(device)) {
      return;
    }

    this.router.navigateByUrl(`/device/${device.deviceId}`);
  }

  private hasDeviceId(device: DeviceDto | undefined): boolean {
    return (
      typeof device?.deviceId === 'string' && device.deviceId.trim().length > 0
    );
  }
}
